import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Pagination, Table } from 'tea-component';
import { useIntl } from 'umi';
import MarqueeBanner from '@intelligence/components/MarqueeBanner';
import Loading from '@intelligence/components/Loading';
import ExportTable from '@intelligence/components/ExportTable';
import SearchRecent from '@intelligence/components/SearchRecent';
import '@intelligence/v3/filterControls';
import Filter from '@intelligence/v3/components/Filter';
import useFilterablePage from '@intelligence/v3/_hooks/useFilterablePage';
import { FilterMergeStrategy } from '@intelligence/v3/_hooks/useFilterablePage/enums';
import usePageLog from '@intelligence/v3/_hooks/usePageLog';
import useGenreSourcesOptions from '@intelligence/components/GenreSelectMiddleware/_hooks/useGenreSourcesOptions';
import { dbiTopChartsBtnLogMes } from '@intelligence/utils/topChartsBuriedPointCfg';
import { dbiPageLogMes } from '@intelligence/utils/buriedPointCfg';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import { seachRecordListPlus } from '@intelligence/services/common';
import { LANGUAGE_CN } from '@home/_config/config';
import {
  ALINEA_CALIBRATION_BUSINESS_TYPE,
  ALINEA_CALIBRATION_DOWNLOAD_KEY,
  ALINEA_CALIBRATION_FILTER_SAVE_TYPE,
  ALINEA_CALIBRATION_MAX_DOWNLOAD_ROWS,
  ALINEA_CALIBRATION_PAGE_ID,
  ALINEA_CALIBRATION_PAGE_LOG_KEY,
  ALINEA_CALIBRATION_PAGE_SIZE_OPTIONS,
  DEFAULT_PAGING,
  DEFAULT_SORT_STATE,
} from './constants';
import { fetchAlineaCalibrationList, fetchAlineaCalibrationRegionCodes } from './services';
import {
  ALINEA_FILTER_GROUPS,
  buildAlineaCalibrationDownloadParams,
  buildAlineaCalibrationListParams,
  formatRegionCodesToSelectOptions,
  getDefaultFilterValues,
  getDownloadTotalFromResponse,
  getFilterValuesWithDefaultRegions,
  getFilterValuesWithReqDate,
  getAlineaPublisherClassOption,
  getRecordsFromResponse,
  getTotalFromResponse,
  hasSelectedTime,
  getAlineaCalibrationFilterItems,
  transformAlineaSearchRecords,
} from './utils';
import { useAlineaCalibrationColumns } from './columns';

import type { PagingQuery } from 'tea-component';
import type {
  IAlineaCalibrationFilterValues,
  IAlineaCalibrationPaging,
  IAlineaCalibrationRecord,
  IAlineaCalibrationSortState,
} from './types';

import './index.scss';

const { scrollable, injectable } = Table.addons;

const AlineaAnalyticsCalibration: React.FC = () => {
  const { locale, formatMessage } = useIntl();
  const [regionCodes, setRegionCodes] = useState<string[]>([]);
  const [regionLoading, setRegionLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filterValues, setFilterValues] = useState<IAlineaCalibrationFilterValues>(getDefaultFilterValues());
  const [resetFilterValues, setResetFilterValues] = useState<IAlineaCalibrationFilterValues>(getDefaultFilterValues());
  const [records, setRecords] = useState<IAlineaCalibrationRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [downloadTotal, setDownloadTotal] = useState(0);
  const [paging, setPaging] = useState<IAlineaCalibrationPaging>(DEFAULT_PAGING);
  const [sortState, setSortState] = useState<IAlineaCalibrationSortState>(DEFAULT_SORT_STATE);
  const [searchUnifiedIds, setSearchUnifiedIds] = useState<string[]>([]);
  const initializedRef = useRef(false);
  const tableListForClearSearchRef = useRef<IAlineaCalibrationRecord[]>([]);
  const totalForClearSearchRef = useRef(0);
  const downloadTotalForClearSearchRef = useRef(0);
  const pagingForClearSearchRef = useRef<IAlineaCalibrationPaging>(DEFAULT_PAGING);

  const { options: genreOptions, loading: genreOptionsLoading } = useGenreSourcesOptions({ entityType: 'combine' });
  const {
    loadingStd: condLoading,
    metricTableRegionCountry: publisherRegionOptions,
    publisherClassCombine,
  } = useQueryDefinition();

  const publisherClassOptions = useMemo(() => {
    const options = Array.isArray(publisherClassCombine) ? publisherClassCombine : [];

    return options.map((item: string) => {
      const option = getAlineaPublisherClassOption(item);
      return {
        ...option,
        text: locale === LANGUAGE_CN ? option.textCN : option.text,
      };
    });
  }, [locale, publisherClassCombine]);

  usePageLog(ALINEA_CALIBRATION_PAGE_LOG_KEY);

  useEffect(() => {
    let isMounted = true;
    setRegionLoading(true);

    fetchAlineaCalibrationRegionCodes()
      .then((codes) => {
        if (!isMounted) {
          return;
        }

        setRegionCodes(codes);
      })
      .finally(() => {
        if (isMounted) {
          setRegionLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const regionOptions = useMemo(() => {
    return formatRegionCodesToSelectOptions(
      regionCodes,
      Array.isArray(publisherRegionOptions) ? publisherRegionOptions : [],
    );
  }, [publisherRegionOptions, regionCodes]);

  const items = useMemo(() => {
    return getAlineaCalibrationFilterItems({
      genreOptions,
      regionOptions,
      publisherClassOptions,
      publisherRegionOptions: Array.isArray(publisherRegionOptions) ? publisherRegionOptions : [],
    });
  }, [genreOptions, regionOptions, publisherClassOptions, publisherRegionOptions]);

  const hookReady = !condLoading && !regionLoading && !genreOptionsLoading;

  const { getInitialFilterValues, syncFilterState } = useFilterablePage({
    pageId: ALINEA_CALIBRATION_PAGE_ID,
    items,
    defaultValues: getDefaultFilterValues(regionOptions),
    mergeStrategy: FilterMergeStrategy.MERGE_BY_PRIORITY,
    ready: hookReady,
    syncTargetConfig: {
      shortLink: {
        enabled: true,
      },
      inherit: {
        enabled: true,
      },
    },
    conditions: {
      genreOptionsMap: genreOptions,
    },
  });

  const syncCurrentFilterState = async (values: IAlineaCalibrationFilterValues): Promise<void> => {
    try {
      const result = await syncFilterState(values);
      if (result?.hasError) {
        console.debug('[dbi] [AlineaAnalyticsCalibration] sync filter state failed', result);
      }
    } catch (error) {
      console.debug('[dbi] [AlineaAnalyticsCalibration] sync filter state failed', error);
    }
  };

  const requestList = async (params: {
    values: IAlineaCalibrationFilterValues;
    paging: IAlineaCalibrationPaging;
    sortState: IAlineaCalibrationSortState;
    keyword?: string;
    unifiedIds?: string[];
    allowReqDateBackfill?: boolean;
    syncAfterRequest?: boolean;
  }): Promise<void> => {
    const { values, paging: requestPaging, sortState: requestSortState, keyword = '', unifiedIds = [] } = params;
    setLoading(true);
    try {
      const listParams = buildAlineaCalibrationListParams({
        items,
        filterValues: values,
        regionOptions,
        paging: requestPaging,
        sortState: requestSortState,
        keyword,
        unifiedIds,
      });
      const res = await fetchAlineaCalibrationList(listParams);
      const nextRecords = getRecordsFromResponse(res);
      const nextTotal = getTotalFromResponse(res);
      const nextDownloadTotal = getDownloadTotalFromResponse(res);
      setRecords(nextRecords);
      setTotal(nextTotal);
      setDownloadTotal(nextDownloadTotal);
      if (!unifiedIds.length) {
        tableListForClearSearchRef.current = nextRecords;
        totalForClearSearchRef.current = nextTotal;
        downloadTotalForClearSearchRef.current = nextDownloadTotal;
        pagingForClearSearchRef.current = requestPaging;
      }

      let nextSyncFilterValues = values;
      if (params.allowReqDateBackfill && !hasSelectedTime(values)) {
        const nextFilterValues = getFilterValuesWithReqDate(values, res?.req);
        nextSyncFilterValues = nextFilterValues;
        setFilterValues(nextFilterValues);
        setResetFilterValues(nextFilterValues);
      }

      if (params.syncAfterRequest) {
        await syncCurrentFilterState(nextSyncFilterValues);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (!hookReady) {
      if (!initializedRef.current) {
        setInitializing(true);
      }
      return () => {
        isMounted = false;
      };
    }

    if (initializedRef.current) {
      setInitializing(false);
      return () => {
        isMounted = false;
      };
    }

    setInitializing(true);
    getInitialFilterValues()
      .then((values) => {
        if (!isMounted) {
          return;
        }

        const nextValues = getFilterValuesWithDefaultRegions(values as IAlineaCalibrationFilterValues, regionOptions);
        initializedRef.current = true;
        setFilterValues(nextValues);
        setResetFilterValues(nextValues);
        requestList({
          values: nextValues,
          paging: DEFAULT_PAGING,
          sortState: DEFAULT_SORT_STATE,
          allowReqDateBackfill: !hasSelectedTime(nextValues),
          syncAfterRequest: true,
        });
      })
      .finally(() => {
        if (isMounted) {
          setInitializing(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [hookReady, items]);

  const handleConfirm = (values: IAlineaCalibrationFilterValues): void => {
    const nextPaging = { ...DEFAULT_PAGING };
    setSearchUnifiedIds([]);
    setFilterValues(values);
    setPaging(nextPaging);
    void syncCurrentFilterState(values);
    requestList({
      values,
      paging: nextPaging,
      sortState,
    });
  };

  const handleReset = (values: IAlineaCalibrationFilterValues): void => {
    void values;
  };

  const handlePagingChange = (query: PagingQuery): void => {
    const nextPaging = {
      page: query?.pageIndex || DEFAULT_PAGING.page,
      page_size: query?.pageSize || DEFAULT_PAGING.page_size,
    };
    setPaging(nextPaging);
    requestList({
      values: filterValues,
      paging: nextPaging,
      sortState,
      unifiedIds: searchUnifiedIds,
    });
  };

  const handleSortChange = (nextSortState: IAlineaCalibrationSortState): void => {
    const nextPaging = { ...paging, page: 1 };
    setSortState(nextSortState);
    setPaging(nextPaging);
    requestList({
      values: filterValues,
      paging: nextPaging,
      sortState: nextSortState,
      unifiedIds: searchUnifiedIds,
    });
  };

  const handleSearchResultList = (id: string): void => {
    const nextPaging = { ...paging, page: 1 };
    const nextUnifiedIds = id ? [id] : [];
    setSearchUnifiedIds(nextUnifiedIds);
    setPaging(nextPaging);
    requestList({
      values: filterValues,
      paging: nextPaging,
      sortState,
      unifiedIds: nextUnifiedIds,
    });
  };

  const handleClearSearch = (): void => {
    setSearchUnifiedIds([]);
    setPaging(pagingForClearSearchRef.current);
    setRecords(tableListForClearSearchRef.current);
    setTotal(totalForClearSearchRef.current);
    setDownloadTotal(downloadTotalForClearSearchRef.current);
  };

  const columns = useAlineaCalibrationColumns({
    sortState,
    onSortChange: handleSortChange,
    regionOptions,
    genreOptions,
  });

  const searchFetchParams = useMemo(() => {
    const params = buildAlineaCalibrationListParams({
      items,
      filterValues,
      regionOptions,
      paging: DEFAULT_PAGING,
      sortState,
      keyword: '',
      unifiedIds: [],
    });

    return {
      ...params,
      filter_ids: [],
      page: 1,
    };
  }, [items, filterValues, regionOptions, sortState]);

  const downloadParams = useMemo(() => {
    return buildAlineaCalibrationDownloadParams({
      items,
      filterValues,
      regionOptions,
      regionsCountries: Array.isArray(publisherRegionOptions) ? publisherRegionOptions : [],
      genreOptionsMap: genreOptions,
      sortState,
      keyword: '',
      unifiedIds: searchUnifiedIds,
    });
  }, [items, filterValues, regionOptions, publisherRegionOptions, genreOptions, sortState, searchUnifiedIds]);
  let downloadModalSize = 600;
  if (locale === LANGUAGE_CN) {
    downloadModalSize = 420;
  }

  const pageLoading = initializing || loading;
  const showPagination = !pageLoading && total > ALINEA_CALIBRATION_PAGE_SIZE_OPTIONS[0];
  let tableRecords = records;
  if (pageLoading) {
    tableRecords = [];
  }
  let tableMinHeight = 'auto';
  if (pageLoading || tableRecords.length === 0) {
    tableMinHeight = '158px';
  }

  return (
    <div className="dbi-alinea-calibration">
      <MarqueeBanner />
      <Card className="dbi-alinea-calibration__filter-card">
        <Card.Body>
          <Filter
            useSticky
            useActionBar
            useSaveFilter
            stickyPortalContainer=".dbi-body > .tea-layout__content-inner"
            loading={initializing || regionLoading || genreOptionsLoading}
            saveFilterType={ALINEA_CALIBRATION_FILTER_SAVE_TYPE}
            filterGroups={ALINEA_FILTER_GROUPS}
            items={items}
            resetValue={resetFilterValues}
            value={filterValues}
            onChange={(_field, _fieldValue, values) => {
              void values;
            }}
            onConfirm={handleConfirm}
            onReset={handleReset}
          />
        </Card.Body>
      </Card>
      <div className="dbi-alinea-calibration__toolbar">
        <div className="dbi-alinea-calibration__search">
          <SearchRecent
            searchType="alinea-analytics-calibration"
            fetchParams={searchFetchParams}
            handleClear={handleClearSearch}
            getTableResultList={handleSearchResultList}
            searchListApi={fetchAlineaCalibrationList}
            useSearchRecentFun={seachRecordListPlus}
            pageLogInfo={dbiPageLogMes[ALINEA_CALIBRATION_PAGE_LOG_KEY]}
            initSearchInput={!(searchUnifiedIds.length > 0)}
            transTableDataForSearch={transformAlineaSearchRecords}
          />
        </div>
        <ExportTable
          entityType="combine"
          classModalName="dbi-alinea-calibration-download-modal"
          businessType={ALINEA_CALIBRATION_BUSINESS_TYPE}
          downloadKey={ALINEA_CALIBRATION_DOWNLOAD_KEY}
          tableParams={downloadParams}
          total={downloadTotal}
          maxSize={ALINEA_CALIBRATION_MAX_DOWNLOAD_ROWS}
          isDisableDownload={pageLoading || records?.length === 0}
          isOnlyExportAllData
          useWebDownload
          downloadModalSize={downloadModalSize}
          newStyleFileName={downloadParams?.new_style_donwload_file_name}
          downloadBtnLogConfig={dbiTopChartsBtnLogMes.combine_PCConsoleAlineaAnalyticsCalibration_TopChartsDownloadBtn}
          downloadLogConfig={dbiTopChartsBtnLogMes.combine_PCConsoleAlineaAnalyticsCalibration_TopChartsDownload}
        />
      </div>
      <Card className="dbi-alinea-calibration__table-card">
        <Card.Body>
          <div className="dbi-alinea-calibration__table-wrap">
            {pageLoading && (
              <div className="dbi-alinea-calibration__table-loading">
                <Loading />
              </div>
            )}
            <Table
              className="dbi-table-sticky dbi-alinea-calibration__table"
              recordKey="rank"
              records={tableRecords}
              columns={columns}
              addons={[
                scrollable({
                  minWidth: 1540,
                  maxHeight: '100%',
                  minHeight: tableMinHeight,
                }),
                injectable({
                  cell: (props, context) => {
                    if (context?.recordIndex === -1 && context?.column?.key === 'date') {
                      return { className: 'dbi-alinea-calibration-table-date-th' };
                    }
                    return {};
                  },
                }),
              ]}
            />
            {!pageLoading && tableRecords.length === 0 && (
              <div className="dbi-alinea-calibration__empty">{formatMessage({ id: 'common_nodata' })}</div>
            )}
          </div>
          {showPagination && (
            <Pagination
              pageSizeOptions={ALINEA_CALIBRATION_PAGE_SIZE_OPTIONS}
              pageIndex={paging.page}
              pageSize={paging.page_size}
              recordCount={total}
              stateTextVisible
              pageSizeVisible
              pageIndexVisible
              jumpVisible
              endJumpVisible
              stateText={formatMessage({ id: 'dbi_pagination_total' }, { total })}
              onPagingChange={handlePagingChange}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AlineaAnalyticsCalibration;
